import { reaction, runInAction } from "mobx"

import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { ManualClPoolTicksResponseItem } from "@/domains/validators"
import { OrchestratedQuery } from "@/domains/library"
import {
  ManualClLiquidityDecreasedEvent,
  ManualClLiquidityIncreasedEvent,
  ManualClPositionBurnedEvent,
  ManualClPositionCreatedEvent,
} from "@/domains/thick-v2/events"

import type { ThickV2TickDistributionGraphQlTransport } from "./thick-v2-tick-distribution-graphql-transport"
import type { ThickV2TickDistributionStore } from "./thick-v2-tick-distribution-store"

/**
 * Drives `ThickV2TickDistributionStore` via an OrchestratedQuery keyed on the
 * active pool address plus the "current-tick quarter" — a low-resolution bucket
 * that triggers re-fetching only when the pool's spot price drifts far enough
 * that the distribution viewport needs to widen.
 *
 * Refreshes are also triggered by the four liquidity-mutating events so the
 * distribution reflects the user's own action immediately.
 */
export class ThickV2TickDistributionOrchestrator {
  private query: OrchestratedQuery<Array<ManualClPoolTicksResponseItem>> | undefined

  private reactionDisposer: (() => void) | undefined
  private positionCreatedHandler: ((event: ManualClPositionCreatedEvent) => void) | undefined
  private liquidityIncreasedHandler: ((event: ManualClLiquidityIncreasedEvent) => void) | undefined
  private liquidityDecreasedHandler: ((event: ManualClLiquidityDecreasedEvent) => void) | undefined
  private positionBurnedHandler: ((event: ManualClPositionBurnedEvent) => void) | undefined

  private currentKey: string | undefined

  constructor(
    private readonly distributionStore: ThickV2TickDistributionStore,
    private readonly graphQlTransport: ThickV2TickDistributionGraphQlTransport,
    private readonly eventBus: IEventBus,
    private readonly orchestratedQueryStore: OrchestratedQueryStore,
    private readonly getCurrentTick: () => number | undefined,
    private readonly chainId: number,
  ) {}

  initialize(): void {
    this.query = new OrchestratedQuery<Array<ManualClPoolTicksResponseItem>>(
      "ThickV2 Tick Distribution GraphQL Sync",
      (sender) => this.onLoad(sender),
      60_000,
      this.onSuccess.bind(this),
      this.onFailure.bind(this),
      this.orchestratedQueryStore,
    )

    this.positionCreatedHandler = this.forceRefresh.bind(this)
    this.liquidityIncreasedHandler = this.forceRefresh.bind(this)
    this.liquidityDecreasedHandler = this.forceRefresh.bind(this)
    this.positionBurnedHandler = this.forceRefresh.bind(this)

    this.eventBus.subscribe(ManualClPositionCreatedEvent.TYPE, this.positionCreatedHandler)
    this.eventBus.subscribe(ManualClLiquidityIncreasedEvent.TYPE, this.liquidityIncreasedHandler)
    this.eventBus.subscribe(ManualClLiquidityDecreasedEvent.TYPE, this.liquidityDecreasedHandler)
    this.eventBus.subscribe(ManualClPositionBurnedEvent.TYPE, this.positionBurnedHandler)

    /**
     * React to active-pool or coarse-tick changes by re-fetching. The coarse
     * bucket is `currentTick >> 8` (≈ 256-tick quarters); this prevents thrash
     * while still keeping the chart viewport anchored on price moves.
     */
    this.reactionDisposer = reaction(
      () => this.composeKey(),
      (nextKey) => {
        if (nextKey === this.currentKey) return
        this.currentKey = nextKey
        if (!this.distributionStore.activePoolAddress) {
          runInAction(() => this.distributionStore.setTicks([]))
          return
        }
        this.query?.forceExecute()
      },
      { fireImmediately: true },
    )

    this.query.start()
  }

  dispose(): void {
    if (this.positionCreatedHandler) {
      this.eventBus.unsubscribe(ManualClPositionCreatedEvent.TYPE, this.positionCreatedHandler)
    }
    if (this.liquidityIncreasedHandler) {
      this.eventBus.unsubscribe(ManualClLiquidityIncreasedEvent.TYPE, this.liquidityIncreasedHandler)
    }
    if (this.liquidityDecreasedHandler) {
      this.eventBus.unsubscribe(ManualClLiquidityDecreasedEvent.TYPE, this.liquidityDecreasedHandler)
    }
    if (this.positionBurnedHandler) {
      this.eventBus.unsubscribe(ManualClPositionBurnedEvent.TYPE, this.positionBurnedHandler)
    }
    this.reactionDisposer?.()
    this.reactionDisposer = undefined
    this.query?.dispose()
    this.query = undefined
  }

  get queryHandle(): OrchestratedQuery<Array<ManualClPoolTicksResponseItem>> | undefined {
    return this.query
  }

  private forceRefresh(): void {
    if (!this.distributionStore.activePoolAddress) return
    this.query?.forceExecute()
  }

  private composeKey(): string {
    const address = this.distributionStore.activePoolAddress ?? "none"
    const tick = this.getCurrentTick()
    const bucket = tick === undefined ? "none" : Math.floor(tick / 256).toString()
    return `${address}:${bucket}`
  }

  private async onLoad(
    _sender: IOrchestratedQuerySender<Array<ManualClPoolTicksResponseItem>>,
  ): Promise<Array<ManualClPoolTicksResponseItem>> {
    const address = this.distributionStore.activePoolAddress
    if (!address) return []
    runInAction(() => this.distributionStore.setIsLoading(true))
    try {
      return await this.graphQlTransport.loadTicks(address, this.chainId)
    } finally {
      runInAction(() => this.distributionStore.setIsLoading(false))
    }
  }

  private onSuccess(
    _sender: IOrchestratedQuerySender<Array<ManualClPoolTicksResponseItem>>,
    data: Array<ManualClPoolTicksResponseItem>,
  ): void {
    runInAction(() => this.distributionStore.setTicks(data))
  }

  private onFailure(
    _sender: IOrchestratedQuerySender<Array<ManualClPoolTicksResponseItem>>,
    error: Error,
  ): void {
    console.error("ThickV2TickDistributionOrchestrator load failed", error)
  }
}
